import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LAMPORTS_PER_SOL, PublicKey } from '@solana/web3.js';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import { getWalletPortfolio, listPools } from '../../services/OrcaService';
import { getRpcForbiddenMessage, readWithRpcFallback, withTimeout } from '../../services/SolanaRpcService';
import './Orca.css';

const SOL_MINT = 'So11111111111111111111111111111111111111112';
const ORCA_MINT = 'orcaEKTdK7LKz57vaAYr9QeNsVEPfiu6QeMU1kektZE';
const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';
const TOKEN_PROGRAM_ID = new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA');
const TOKEN_2022_PROGRAM_ID = new PublicKey('TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb');
const KNOWN_TOKENS = {
    [SOL_MINT]: {
        mint: SOL_MINT,
        symbol: 'SOL',
        name: 'Solana',
        imageUrl: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png',
        decimals: 9
    },
    [ORCA_MINT]: {
        mint: ORCA_MINT,
        symbol: 'ORCA',
        name: 'Orca',
        imageUrl: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/orcaEKTdK7LKz57vaAYr9QeNsVEPfiu6QeMU1kektZE/logo.png',
        decimals: 6
    },
    [USDC_MINT]: {
        mint: USDC_MINT,
        symbol: 'USDC',
        name: 'USD Coin',
        imageUrl: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v/logo.png',
        decimals: 6
    }
};

function OrcaPortfolio() {
    const navigate = useNavigate();
    const { connection } = useConnection();
    const { connected, publicKey, wallet } = useWallet();
    const [assets, setAssets] = useState([]);
    const [tokenMap, setTokenMap] = useState({});
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({ type: '', text: '' });

    useEffect(() => {
        loadTokenMetadata();
    }, []);

    useEffect(() => {
        if (connected && publicKey) loadPortfolio();
        else setAssets([]);
    }, [connected, publicKey]);

    async function loadTokenMetadata() {
        try {
            const pools = await listPools({ limit: 100 });
            const map = { ...KNOWN_TOKENS };
            pools.forEach(pool => {
                [pool.tokenA, pool.tokenB, pool.display?.tokenA, pool.display?.tokenB].filter(Boolean).forEach(token => {
                    map[token.mint] = {
                        mint: token.mint,
                        symbol: token.symbol,
                        name: token.name,
                        imageUrl: token.imageUrl,
                        decimals: token.decimals
                    };
                });
            });
            setTokenMap(map);
        } catch (err) {
            console.error(err);
            setTokenMap(KNOWN_TOKENS);
        }
    }

    async function loadPortfolio() {
        setLoading(true);
        try {
            const portfolio = await getWalletPortfolio(publicKey.toBase58(), {
                mints: Object.keys(KNOWN_TOKENS)
            });
            applyAssets(normalizeBackendAssets(portfolio.assets));
        } catch (err) {
            console.error(err);
            await loadPortfolioFromBrowserRpc(err);
        } finally {
            setLoading(false);
        }
    }

    async function loadPortfolioFromBrowserRpc(originalError) {
        try {
            const { lamports, tokenAccounts } = await readWithRpcFallback(connection, async rpc => {
                const [balanceResult, accountsResult] = await Promise.all([
                    rpc.getBalance(publicKey),
                    getTokenAccounts(rpc)
                ]);
                return {
                    lamports: balanceResult,
                    tokenAccounts: mergeTokenAccounts([
                        ...accountsResult,
                        ...await getKnownMintTokenAccounts(rpc, accountsResult)
                    ])
                };
            });

            const solAsset = {
                mint: SOL_MINT,
                symbol: 'SOL',
                name: 'Solana',
                amount: lamports / LAMPORTS_PER_SOL,
                program: 'native'
            };

            const tokenAssets = tokenAccounts
                .map(item => {
                    const info = item.account.data.parsed.info;
                    const metadata = tokenMap[info.mint] || KNOWN_TOKENS[info.mint] || {};
                    return {
                        mint: info.mint,
                        symbol: metadata.symbol || shortAddress(info.mint),
                        name: metadata.name || 'Token Solana',
                        imageUrl: metadata.imageUrl,
                        amount: Number(info.tokenAmount.uiAmountString || info.tokenAmount.uiAmount || 0),
                        decimals: info.tokenAmount.decimals,
                        program: item.account.owner.toBase58()
                    };
                })
                .filter(asset => asset.amount > 0);

            applyAssets([solAsset, ...tokenAssets].sort((a, b) => b.amount - a.amount));
        } catch (err) {
            const text = isRpcForbidden(err)
                ? getRpcForbiddenMessage()
                : originalError?.response?.data || originalError?.message || err.response?.data || err.message || 'Erro ao carregar portfólio.';
            setNotification({ type: 'error', text });
        }
    }

    function normalizeBackendAssets(items = []) {
        return items.map(asset => {
            const metadata = tokenMap[asset.mint] || KNOWN_TOKENS[asset.mint] || {};
            return {
                ...asset,
                symbol: metadata.symbol || asset.symbol || shortAddress(asset.mint),
                name: metadata.name || asset.name || 'Token Solana',
                imageUrl: metadata.imageUrl || asset.imageUrl,
                amount: Number(asset.amount || 0)
            };
        }).filter(asset => asset.amount > 0).sort((a, b) => b.amount - a.amount);
    }

    function applyAssets(nextAssets) {
        setAssets(currentAssets => {
            const currentTokenCount = currentAssets.filter(asset => asset.mint !== SOL_MINT).length;
            const nextTokenCount = nextAssets.filter(asset => asset.mint !== SOL_MINT).length;
            if (currentTokenCount > nextTokenCount) return currentAssets;
            return nextAssets;
        });
    }

    async function getTokenAccounts(rpc = connection) {
        const splAccounts = await withTimeout(
            rpc.getParsedTokenAccountsByOwner(publicKey, { programId: TOKEN_PROGRAM_ID }),
            7000,
            'Tempo limite ao buscar tokens SPL.'
        );
        const token2022Accounts = await withTimeout(
            rpc.getParsedTokenAccountsByOwner(publicKey, { programId: TOKEN_2022_PROGRAM_ID }),
            2500,
            'Tempo limite ao buscar tokens Token-2022.'
        ).catch(() => ({ value: [] }));

        return [...splAccounts.value, ...token2022Accounts.value];
    }

    async function getKnownMintTokenAccounts(rpc, existingAccounts = []) {
        const existingMints = new Set(existingAccounts.map(item => item.account.data.parsed.info.mint));
        const knownMints = Object.keys(KNOWN_TOKENS)
            .filter(mint => mint !== SOL_MINT && !existingMints.has(mint) && isValidPublicKey(mint))
            .slice(0, 30);

        const results = await Promise.allSettled(knownMints.map(mint => withTimeout(
            rpc.getParsedTokenAccountsByOwner(publicKey, { mint: new PublicKey(mint) }),
            2500,
            `Tempo limite ao buscar token ${mint}.`
        )));

        return results
            .filter(result => result.status === 'fulfilled')
            .flatMap(result => result.value.value);
    }

    function mergeTokenAccounts(accounts) {
        const map = new Map();
        accounts.forEach(item => {
            const key = item.pubkey?.toBase58 ? item.pubkey.toBase58() : `${item.account.owner.toBase58()}-${item.account.data.parsed.info.mint}`;
            map.set(key, item);
        });
        return [...map.values()];
    }

    function isRpcForbidden(err) {
        const text = `${err?.message || ''} ${JSON.stringify(err || {})}`.toLowerCase();
        return text.includes('403') || text.includes('forbidden');
    }

    function isValidPublicKey(value) {
        try {
            new PublicKey(value);
            return true;
        } catch (err) {
            return false;
        }
    }

    function shortAddress(address) {
        if (!address) return '';
        return `${address.slice(0, 4)}...${address.slice(-4)}`;
    }

    function formatNumber(value, maximumFractionDigits = 8) {
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) return value || '-';
        return parsed.toLocaleString('pt-BR', { maximumFractionDigits });
    }

    function tokenIcon(asset) {
        const metadata = tokenMap[asset.mint] || KNOWN_TOKENS[asset.mint] || {};
        const imageUrl = metadata.imageUrl || asset.imageUrl;
        const symbol = metadata.symbol || asset.symbol;
        if (imageUrl) return <img src={imageUrl} alt={symbol} />;
        return <span>{(symbol || '?').slice(0, 1)}</span>;
    }

    return (
        <>
            <Menu />
            <main className="content orca-page">
                <div className="orca-container">
                    <section className="orca-hero">
                        <div className="orca-hero-copy">
                            <span className="orca-eyebrow">Wallet Portfolio</span>
                            <h1>Seu portfólio Solana</h1>
                            <p>Veja SOL, SPL tokens e Token-2022 disponíveis na carteira conectada.</p>
                            <div className="orca-hero-actions">
                                <button className="orca-primary-btn" onClick={loadPortfolio} disabled={!connected || loading}>
                                    {loading ? 'Atualizando...' : 'Atualizar portfólio'}
                                </button>
                                <button className="orca-ghost-btn" onClick={() => navigate('/orca/positions')}>
                                    Minhas posições
                                </button>
                            </div>
                        </div>
                        <div className="orca-wallet-panel">
                            <WalletMultiButton />
                            <div className="orca-wallet-status">
                                <strong>{connected ? 'Carteira conectada' : 'Conectar carteira'}</strong>
                                <span>{connected && publicKey ? shortAddress(publicKey.toBase58()) : 'Phantom, Solflare ou WalletConnect'}</span>
                                {wallet?.adapter?.name ? <small>{wallet.adapter.name}</small> : <small>Selecione sua carteira.</small>}
                            </div>
                        </div>
                    </section>

                    <section className="orca-market-card portfolio-card">
                        <div className="orca-section-header">
                            <div>
                                <h2>Ativos</h2>
                                <p>{assets.length} ativo(s) encontrado(s)</p>
                            </div>
                        </div>

                        {connected ? (
                            <div className="portfolio-list">
                                {assets.map(asset => (
                                    <div className="portfolio-row" key={`${asset.program}-${asset.mint}`}>
                                        <div className="orca-token-pair">
                                            <div className="orca-token-icon">{tokenIcon(asset)}</div>
                                            <div>
                                                <strong>{tokenMap[asset.mint]?.symbol || KNOWN_TOKENS[asset.mint]?.symbol || asset.symbol}</strong>
                                                <small>{tokenMap[asset.mint]?.name || KNOWN_TOKENS[asset.mint]?.name || asset.name} · {shortAddress(asset.mint)}</small>
                                            </div>
                                        </div>
                                        <div className="orca-pool-metric">
                                            <span>{formatNumber(asset.amount, 8)}</span>
                                            <small>Saldo</small>
                                        </div>
                                    </div>
                                ))}
                                {!assets.length && <div className="orca-empty-state">Nenhum saldo encontrado.</div>}
                            </div>
                        ) : (
                            <div className="orca-empty-state">Conecte uma carteira para ver seu portfólio.</div>
                        )}
                    </section>
                </div>
                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
        </>
    );
}

export default OrcaPortfolio;
